import React, { useState } from 'react';
import {
  Box,
  VStack,
  HStack,
  Text,
  Button,
  FormControl,
  FormLabel,
  Input,
  Textarea,
  Select,
  useToast,
  useColorModeValue,
  Container,
  Heading,
  Divider,
  Progress,
  IconButton
} from '@chakra-ui/react';
import { FiUpload, FiX, FiArrowLeft } from 'react-icons/fi';
import IconWrapper from '../components/IconWrapper';
import SeriesSeasonPicker from '../components/SeriesSeasonPicker';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';

interface VideoUploadData {
  title: string;
  description: string;
  videoFile: File | null;
  thumbnailFile: File | null;
  genre: string;
  contentRating: string;
  language: string;
  tags: string;
  seriesId: string | null;
  seasonId: string | null;
}

const UploadVideo: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const toast = useToast();

  const [formData, setFormData] = useState<VideoUploadData>({
    title: '',
    description: '',
    videoFile: null,
    thumbnailFile: null,
    genre: '',
    contentRating: 'G',
    language: 'English',
    tags: '',
    seriesId: null,
    seasonId: null
  });

  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);

  const bgColor = useColorModeValue('white', 'gray.800');
  const textColor = useColorModeValue('gray.900', 'white');
  const labelColor = useColorModeValue('gray.600', 'gray.400');
  const borderColor = useColorModeValue('gray.200', 'gray.600');
  const inputBg = useColorModeValue('white', 'gray.700');

  const handleInputChange = (field: keyof VideoUploadData, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleVideoSelect = (file: File) => {
    handleInputChange('videoFile', file);
  };

  const handleThumbnailSelect = (file: File) => {
    handleInputChange('thumbnailFile', file);
  };

  const handleNewSeries = (seriesData: { title: string; description: string }) => {
    // TODO: Create new series in database
    toast({
      title: 'Series Created',
      description: `"${seriesData.title}" has been created`,
      status: 'success',
      duration: 3000,
      isClosable: true,
    });
  };

  const handleNewSeason = (seasonData: { title: string; description: string }) => {
    // TODO: Create new season in database
    toast({
      title: 'Season Created',
      description: `"${seasonData.title}" has been created`,
      status: 'success',
      duration: 3000,
      isClosable: true,
    });
  };

  const handleSubmit = async () => {
    if (!user) {
      toast({
        title: 'Authentication Required',
        description: 'Please sign in to upload content',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    if (!formData.title.trim()) {
      toast({
        title: 'Title Required',
        description: 'Please enter a title for your video',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    if (!formData.videoFile) {
      toast({
        title: 'Video Required',
        description: 'Please select a video file to upload',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    try {
      setUploading(true);
      setUploadProgress(0);

      // TODO: Implement actual upload logic
      // Simulate upload progress
      for (let i = 0; i <= 100; i += 10) {
        setUploadProgress(i);
        await new Promise(resolve => setTimeout(resolve, 100));
      }

      toast({
        title: 'Upload Successful',
        description: 'Your video has been uploaded successfully',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });

      // Navigate based on whether it's part of a series
      if (formData.seriesId && formData.seasonId) {
        navigate(`/series/${formData.seriesId}`);
      } else {
        navigate('/dashboard');
      }
    } catch (error) {
      console.error('Upload error:', error);
      toast({
        title: 'Upload Failed',
        description: 'There was an error uploading your video',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setUploading(false);
    }
  };

  return (
    <Container maxW="container.lg" py={8}>
      <VStack gap={6} align="stretch">
        {/* Header */}
        <HStack gap={4} align="center">
          <IconButton
            aria-label="Go back"
            icon={<IconWrapper icon={FiArrowLeft} size={20} />}
            variant="ghost"
            onClick={() => navigate(-1)}
          />
          <Heading size="lg" color={textColor}>
            Upload Video
          </Heading>
        </HStack>

        <Divider />

        {/* Upload Progress */}
        {uploading && (
          <Box>
            <Text color={labelColor} mb={2}>
              Uploading... {uploadProgress}%
            </Text>
            <Progress value={uploadProgress} colorScheme="purple" />
          </Box>
        )}

        {/* Form */}
        <Box bg={bgColor} p={6} borderRadius="lg" border="1px solid" borderColor={borderColor}>
          <VStack gap={6} align="stretch">
            {/* Basic Info */}
            <VStack gap={4} align="stretch">
              <Heading size="md" color={textColor}>
                Video Information
              </Heading>
              
              <FormControl>
                <FormLabel color={labelColor} fontWeight="bold">
                  Title *
                </FormLabel>
                <Input
                  value={formData.title}
                  onChange={(e) => handleInputChange('title', e.target.value)}
                  placeholder="Enter video title"
                  bg={inputBg}
                  borderColor={borderColor}
                />
              </FormControl>

              <FormControl>
                <FormLabel color={labelColor} fontWeight="bold">
                  Description
                </FormLabel>
                <Textarea
                  value={formData.description}
                  onChange={(e) => handleInputChange('description', e.target.value)}
                  placeholder="Describe your video"
                  bg={inputBg}
                  borderColor={borderColor}
                  rows={3}
                />
              </FormControl>

              <HStack gap={4} align="stretch">
                <FormControl>
                  <FormLabel color={labelColor} fontWeight="bold">
                    Genre
                  </FormLabel>
                  <Select
                    value={formData.genre}
                    onChange={(e) => handleInputChange('genre', e.target.value)}
                    bg={inputBg}
                    borderColor={borderColor}
                  >
                    <option value="">Select genre</option>
                    <option value="action">Action</option>
                    <option value="comedy">Comedy</option>
                    <option value="drama">Drama</option>
                    <option value="horror">Horror</option>
                    <option value="romance">Romance</option>
                    <option value="sci-fi">Sci-Fi</option>
                    <option value="thriller">Thriller</option>
                  </Select>
                </FormControl>

                <FormControl>
                  <FormLabel color={labelColor} fontWeight="bold">
                    Content Rating
                  </FormLabel>
                  <Select
                    value={formData.contentRating}
                    onChange={(e) => handleInputChange('contentRating', e.target.value)}
                    bg={inputBg}
                    borderColor={borderColor}
                  >
                    <option value="G">G</option>
                    <option value="PG">PG</option>
                    <option value="PG-13">PG-13</option>
                    <option value="R">R</option>
                    <option value="NC-17">NC-17</option>
                  </Select>
                </FormControl>
              </HStack>

              <HStack gap={4} align="stretch">
                <FormControl>
                  <FormLabel color={labelColor} fontWeight="bold">
                    Language
                  </FormLabel>
                  <Input
                    value={formData.language}
                    onChange={(e) => handleInputChange('language', e.target.value)}
                    placeholder="English"
                    bg={inputBg}
                    borderColor={borderColor}
                  />
                </FormControl>

                <FormControl>
                  <FormLabel color={labelColor} fontWeight="bold">
                    Tags
                  </FormLabel>
                  <Input
                    value={formData.tags}
                    onChange={(e) => handleInputChange('tags', e.target.value)}
                    placeholder="comedy, action, adventure"
                    bg={inputBg}
                    borderColor={borderColor}
                  />
                </FormControl>
              </HStack>
            </VStack>

            <Divider />

            {/* Series/Season Selection */}
            <VStack gap={4} align="stretch">
              <Heading size="md" color={textColor}>
                Series & Season (Optional)
              </Heading>
              <Text color={labelColor} fontSize="sm">
                Add this video to an existing series and season, or create new ones
              </Text>
              
              <SeriesSeasonPicker
                selectedSeriesId={formData.seriesId}
                selectedSeasonId={formData.seasonId}
                onSeriesChange={(seriesId) => handleInputChange('seriesId', seriesId)}
                onSeasonChange={(seasonId) => handleInputChange('seasonId', seasonId)}
                onNewSeries={handleNewSeries}
                onNewSeason={handleNewSeason}
                disabled={uploading}
              />
            </VStack>

            <Divider />

            {/* File Uploads */}
            <VStack gap={4} align="stretch">
              <Heading size="md" color={textColor}>
                Files
              </Heading>

              {/* Video Upload */}
              <FormControl>
                <FormLabel color={labelColor} fontWeight="bold">
                  Video File *
                </FormLabel>
                <Box
                  border="2px dashed"
                  borderColor={formData.videoFile ? 'purple.300' : borderColor}
                  borderRadius="lg"
                  p={6}
                  textAlign="center"
                  bg={formData.videoFile ? 'purple.50' : inputBg}
                  _dark={{ bg: formData.videoFile ? 'purple.900' : 'gray.700' }}
                  cursor="pointer"
                  _hover={{ borderColor: 'purple.400' }}
                  transition="all 0.2s"
                  onClick={() => document.getElementById('video-upload')?.click()}
                  minH="120px"
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                >
                  {formData.videoFile ? (
                    <VStack gap={3}>
                      <HStack gap={2}>
                        <IconWrapper icon={FiUpload} size={24} color="purple.500" />
                        <Text color={textColor} fontWeight="medium">
                          {formData.videoFile.name}
                        </Text>
                        <IconButton
                          aria-label="Remove video"
                          icon={<IconWrapper icon={FiX} size={16} />}
                          size="sm"
                          colorScheme="red"
                          variant="ghost"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleInputChange('videoFile', null);
                          }}
                        />
                      </HStack>
                      <Text fontSize="sm" color={labelColor}>
                        Click to change video
                      </Text>
                    </VStack>
                  ) : (
                    <VStack gap={3}>
                      <IconWrapper icon={FiUpload} size={32} color="gray.400" />
                      <Text color={textColor} fontWeight="medium">
                        Click to upload video
                      </Text>
                      <Text fontSize="sm" color={labelColor}>
                        MP4, MOV, AVI up to 2GB
                      </Text>
                    </VStack>
                  )}
                </Box>
                <Input
                  id="video-upload"
                  type="file"
                  accept="video/*"
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) handleVideoSelect(file);
                  }}
                  display="none"
                />
              </FormControl>

              {/* Thumbnail Upload */}
              <FormControl>
                <FormLabel color={labelColor} fontWeight="bold">
                  Thumbnail (Optional)
                </FormLabel>
                <Box
                  border="2px dashed"
                  borderColor={formData.thumbnailFile ? 'purple.300' : borderColor}
                  borderRadius="lg"
                  p={6}
                  textAlign="center"
                  bg={formData.thumbnailFile ? 'purple.50' : inputBg}
                  _dark={{ bg: formData.thumbnailFile ? 'purple.900' : 'gray.700' }}
                  cursor="pointer"
                  _hover={{ borderColor: 'purple.400' }}
                  transition="all 0.2s"
                  onClick={() => document.getElementById('thumbnail-upload')?.click()}
                  minH="120px"
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                >
                  {formData.thumbnailFile ? (
                    <VStack gap={3}>
                      <HStack gap={2}>
                        <IconWrapper icon={FiUpload} size={24} color="purple.500" />
                        <Text color={textColor} fontWeight="medium">
                          {formData.thumbnailFile.name}
                        </Text>
                        <IconButton
                          aria-label="Remove thumbnail"
                          icon={<IconWrapper icon={FiX} size={16} />}
                          size="sm"
                          colorScheme="red"
                          variant="ghost"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleInputChange('thumbnailFile', null);
                          }}
                        />
                      </HStack>
                      <Text fontSize="sm" color={labelColor}>
                        Click to change thumbnail
                      </Text>
                    </VStack>
                  ) : (
                    <VStack gap={3}>
                      <IconWrapper icon={FiUpload} size={32} color="gray.400" />
                      <Text color={textColor} fontWeight="medium">
                        Click to upload thumbnail
                      </Text>
                      <Text fontSize="sm" color={labelColor}>
                        JPG, PNG up to 10MB
                      </Text>
                    </VStack>
                  )}
                </Box>
                <Input
                  id="thumbnail-upload"
                  type="file"
                  accept="image/*"
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) handleThumbnailSelect(file);
                  }}
                  display="none"
                />
              </FormControl>
            </VStack>

            {/* Submit Button */}
            <HStack gap={4} justify="flex-end">
              <Button
                variant="outline"
                onClick={() => navigate(-1)}
                disabled={uploading}
              >
                Cancel
              </Button>
              <Button
                colorScheme="purple"
                onClick={handleSubmit}
                loadingText="Uploading..."
                isLoading={uploading}
                disabled={!formData.title.trim() || !formData.videoFile}
              >
                Upload Video
              </Button>
            </HStack>
          </VStack>
        </Box>
      </VStack>
    </Container>
  );
};

export default UploadVideo;
